import { useQueryClient } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { fleetKeys } from '@/hooks/fleet-keys';
import type { FleetFilterControls } from '@/hooks/use-fleet-filters';
import { fetchServerDetail } from '@/hooks/use-fleet-server';
import { cn } from '@/lib/utils';
import type { ServerListItem } from '@/types/fleet';

import { LastSeen } from './last-seen';
import { ProtocolChip } from './protocol-chip';
import { ProviderBadge } from './provider-badge';
import { RegionBadge } from './region-badge';
import { RowKebab } from './row-kebab';
import { ServerDrawer } from './server-drawer';
import { ServerLink } from './server-link';
import { ServerRowActions } from './server-row-actions';
import { StatusBadge } from './status-badge';

const SORT_FIELD: Record<string, string> = {
  status: 'status',
  name: 'name',
  provider: 'provider',
  region: 'region_country',
  last_seen: 'last_seen_at',
};

const columnHelper = createColumnHelper<ServerListItem>();

/** The Reality stealth role gets a distinct accent treatment. */
function RoleCell({ role }: { role: ServerListItem['role'] }): JSX.Element {
  const label = role.replace(/_/g, ' ');

  if (role === 'v2ray_edge') {
    return (
      <Badge
        variant="active"
        className="lowercase tracking-wide"
        title="Reality stealth edge"
      >
        {label}
      </Badge>
    );
  }

  return <span className="font-mono text-xs text-muted-foreground">{label}</span>;
}

function buildColumns(onOpenActions: (name: string) => void) {
  return [
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (ctx) => <StatusBadge status={ctx.getValue()} />,
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (ctx) => (
        <ServerLink
          name={ctx.getValue()}
          status={ctx.row.original.status}
          className="font-mono text-xs"
        />
      ),
    }),
    columnHelper.accessor('role', {
      header: 'Role',
      cell: (ctx) => <RoleCell role={ctx.getValue()} />,
    }),
    columnHelper.accessor('provider', {
      header: 'Provider',
      cell: (ctx) => <ProviderBadge provider={ctx.getValue()} />,
    }),
    columnHelper.display({
      id: 'region',
      header: 'Region',
      cell: (ctx) => <RegionBadge region={ctx.row.original.region} />,
    }),
    columnHelper.display({
      id: 'last_seen',
      header: 'Last seen',
      cell: (ctx) => <LastSeen timestamp={ctx.row.original.last_seen_at} />,
    }),
    columnHelper.display({
      id: 'protocols',
      header: 'Protocols',
      cell: (ctx) => (
        <div className="flex flex-wrap gap-1">
          {ctx.row.original.protocols.length === 0 ? (
            <span className="text-xs text-muted-foreground">—</span>
          ) : (
            ctx.row.original.protocols.map((p) => (
              <ProtocolChip key={p.protocol} protocol={p.protocol} enabled={p.enabled} />
            ))
          )}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (ctx) => <RowKebab name={ctx.row.original.name} onOpen={onOpenActions} />,
    }),
  ];
}

export function ServerTable({
  rows,
  filters,
}: {
  rows: ServerListItem[];
  filters: FleetFilterControls;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { query, setSort } = filters;
  const [drawerServer, setDrawerServer] = useState<ServerListItem | null>(null);

  // The row ⋮ menu opens the detail page's own ActionMenu for that server
  // (planet-proxy-KB#883). A server's host stays mounted while its menu is open
  // or a run started from it is in flight or tracked, then reports idle and
  // unmounts, so its detail and catalogue queries stop polling.
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [hosts, setHosts] = useState<string[]>([]);
  const openActions = useCallback((name: string) => {
    setHosts((h) => (h.includes(name) ? h : [...h, name]));
    setMenuFor(name);
  }, []);
  const closeActions = useCallback(
    (name: string) => setMenuFor((open) => (open === name ? null : open)),
    [],
  );
  const releaseHost = useCallback(
    (name: string) => setHosts((h) => h.filter((n) => n !== name)),
    [],
  );

  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPrefetch = useRef(0);

  function prefetch(name: string): void {
    if (Date.now() - lastPrefetch.current < 500) return;
    lastPrefetch.current = Date.now();
    void queryClient.prefetchQuery({
      queryKey: fleetKeys.server(name),
      queryFn: ({ signal }) => fetchServerDetail(name, signal),
    });
  }

  const columns = buildColumns(openActions);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  });

  const activeSort = query.sort ?? 'name';
  const activeDesc = activeSort.startsWith('-');
  const activeField = activeDesc ? activeSort.slice(1) : activeSort;

  function onHeaderClick(columnId: string): void {
    const field = SORT_FIELD[columnId];
    if (!field) return;
    setSort(field === activeField && !activeDesc ? `-${field}` : field);
  }

  return (
    <>
      <Table className="border-separate border-spacing-0 overflow-hidden rounded-md border border-border bg-card shadow-panel">
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="hover:bg-transparent">
              {group.headers.map((header) => {
                const field = SORT_FIELD[header.column.id];
                const sortable = Boolean(field);
                const isActive = sortable && field === activeField;

                return (
                  <TableHead
                    key={header.id}
                    className="border-b border-border bg-secondary/40 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onHeaderClick(header.column.id)}
                        className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {!isActive && <ChevronsUpDown className="h-3 w-3 opacity-40" aria-hidden />}
                        {isActive && activeDesc && <ArrowDown className="h-3 w-3" aria-hidden />}
                        {isActive && !activeDesc && <ArrowUp className="h-3 w-3" aria-hidden />}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={cn(
                'cursor-pointer border-0 transition-colors hover:bg-accent/40 [&>td]:border-b [&>td]:border-border',
                row.original.role === 'v2ray_edge' &&
                  'bg-status-active/[0.04] hover:bg-status-active/[0.08]',
                row.original.status === 'decommissioned' && 'opacity-60',
              )}
              onClick={() => setDrawerServer(row.original)}
              onMouseEnter={() => {
                hoverTimer.current = setTimeout(() => prefetch(row.original.name), 200);
              }}
              onMouseLeave={() => {
                if (hoverTimer.current) clearTimeout(hoverTimer.current);
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className="h-11 whitespace-nowrap"
                  onClick={cell.column.id === 'actions' ? (e) => e.stopPropagation() : undefined}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ServerDrawer server={drawerServer} onClose={() => setDrawerServer(null)} />

      {hosts.map((name) => (
        <ServerRowActions
          key={name}
          serverName={name}
          open={menuFor === name}
          onClose={closeActions}
          onIdle={releaseHost}
        />
      ))}
    </>
  );
}
